import { createInternalEventHandler } from '@rio/tracking'
import { stringify } from 'query-string'
import snakeCase from 'lodash/snakeCase'
import { useI18n } from 'builder/hooks/useI18n'
import Icon from 'builder/components/Icon'
import { Buttons, SocialButton, SocialNetwork, ChevronIcon, NetworkIcon } from './styles'
import { PostSignupMode } from 'builder/modules/constants'

type SocialProfileButtonsProps = {
  templateId: string
  documentType: string
  className?: string
  hasSpacing?: boolean
  singleSignFlow?: boolean
  availableSocialNetworks?: {
    facebook: boolean
    google: boolean
    linkedin: boolean
  }
}

const SocialProfileButtons = ({
  documentType,
  templateId,
  className,
  hasSpacing = true,
  singleSignFlow = false,
  availableSocialNetworks = { google: true, facebook: true, linkedin: true },
}: SocialProfileButtonsProps) => {
  const { i18n } = useI18n()

  // "cover-letter" → "cover_letter"
  const type = snakeCase(documentType)

  // Pass some of the persisted values to the social auth controller
  const postSocialSignupRedirectUrl = `/app/post-signup?${stringify({
    mode: PostSignupMode.landing,
    template: templateId,
    type,
  })}`

  const query = stringify({
    type,
    template: templateId,
    on_success_redirect: postSocialSignupRedirectUrl,
  })

  return (
    <Buttons className={className} hasSpacing={hasSpacing}>
      {availableSocialNetworks.facebook && (
        <SocialButton
          href={`/auth/facebook?${query}`}
          onClick={createInternalEventHandler('choose_social_sign_up_method', {
            network: 'facebook',
          })}
        >
          <SocialNetwork>
            <NetworkIcon>
              <Icon.Facebook />
            </NetworkIcon>
            {singleSignFlow
              ? i18n.t('builder.sign_in.methods.continue_facebook')
              : i18n.t('builder.sign_in.methods.facebook')}
          </SocialNetwork>

          <ChevronIcon />
        </SocialButton>
      )}

      {availableSocialNetworks.google && (
        <SocialButton
          href={`/auth/google_oauth2?${query}`}
          onClick={createInternalEventHandler('choose_social_sign_up_method', {
            network: 'google',
          })}
        >
          <SocialNetwork>
            <NetworkIcon>
              <Icon.Google />
            </NetworkIcon>
            {singleSignFlow
              ? i18n.t('builder.sign_in.methods.continue_google')
              : i18n.t('builder.sign_in.methods.google')}
          </SocialNetwork>

          <ChevronIcon />
        </SocialButton>
      )}

      {availableSocialNetworks.linkedin && (
        <SocialButton
          href={`/auth/linkedin?${query}`}
          onClick={createInternalEventHandler('choose_social_sign_up_method', {
            network: 'linkedin',
          })}
        >
          <SocialNetwork>
            <NetworkIcon>
              <Icon.Linkedin />
            </NetworkIcon>
            {singleSignFlow
              ? i18n.t('builder.sign_in.methods.continue_linkedin')
              : i18n.t('builder.sign_in.methods.linkedin')}
          </SocialNetwork>

          <ChevronIcon />
        </SocialButton>
      )}
    </Buttons>
  )
}

export default SocialProfileButtons
